import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getRecommendationsPreview } from '../../../api/recommendations';
import { useRowForm } from '../RowFormContext';
import { PreviewCards } from './PreviewCards';
import './RowPreviewSection.scss';

export const RowPreviewSection = () => {
  const currentUser = useSelector(state => state.users.currentUser);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [preview, setPreview] = useState(null);
  const [username, setUsername] = useState(currentUser?.username || '');
  const [debouncedUsername, setDebouncedUsername] = useState('');
  const [showApiRequest, setShowApiRequest] = useState(false);

  const { entity, filter, cardSort, title } = useRowForm();

  useEffect(() => {
    if (currentUser?.username && !username) {
      setUsername(currentUser.username);
    }
  }, [currentUser, username]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedUsername(username);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [username]);

  useEffect(() => {
    const abortController = new AbortController();

    const fetchPreview = async () => {
      if (!debouncedUsername || !entity) {
        setPreview(null);
        return;
      }

      try {
        setIsLoadingPreview(true);
        setPreview(null);

        const response = await getRecommendationsPreview({
          username: debouncedUsername,
          entity,
          filter,
          cardSort,
          title
        });

        if (!abortController.signal.aborted) {
          setPreview(response);
        }
      } catch (error) {
        if (error?.name !== 'AbortError') {
          console.error('Error fetching preview:', error);
          setPreview(null);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoadingPreview(false);
        }
      }
    };

    fetchPreview();

    return () => abortController.abort();
  }, [debouncedUsername, entity, filter, cardSort]);

  const toggleShowApiRequest = e => {
    e.preventDefault();
    e.stopPropagation();

    setShowApiRequest(prev => !prev);
  };

  return (
    <div className='recommendation-row-preview-section'>
      <div className='form-field'>
        <label htmlFor='username'>Preview</label>
        <input
          id='username'
          name='username'
          type='text'
          placeholder='Enter username to preview'
          value={username}
          onChange={e => setUsername(e.target.value)}
        />
      </div>

      <div className='preview-display'>
        <PreviewCards cards={preview?.data?.cards} title={preview?.data?.title} isLoading={isLoadingPreview} />
      </div>

      <button className='toggle-api-request-button' type='button' onClick={toggleShowApiRequest}>
        {showApiRequest ? 'Hide API request' : 'Show API request'}
      </button>

      {showApiRequest && preview?.requestBody && (
        <div className='api-request-debugging'>
          <h3>Request Body</h3>
          <pre>
            <code>{preview?.requestBody && JSON.stringify(preview.requestBody, null, 2)}</code>
          </pre>

          <h3>cURL Command</h3>
          <pre>
            <code>{preview?.data?.curlCommand}</code>
          </pre>
        </div>
      )}
    </div>
  );
};
